import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { FilePdfOutlined } from "@ant-design/icons";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterRentManual from "../components/RegisterRentManual";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";
import * as Common from "../commons/common";

type DataIndex = keyof Types.RentManualDto;

function RentManualList() {
    const alert = useAlert();

    const [rentManuals, setRentManuals] = useState<Array<Types.RentManualDto>>();
    const [currentRentManual, setcurrentRentManual] = useState<Types.RentManualDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteRentManual, setOpenDeleteRentManual] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.RentManualDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.RentManualDto> = [
        {
            align: "center",
            title: String.manualId,
            dataIndex: "rentManualId",
            key: "rentManualId",
            ...getColumnSearchProps("rentManualId"),
        },
        {
            align: "center",
            title: String.manualVersion,
            dataIndex: "rentManualVersion",
            key: "rentManualVersion",
        },
        {
            align: "center",
            title: String.manualName,
            dataIndex: "rentManualName",
            key: "rentManualName",
            ...getColumnSearchProps("rentManualName"),
        },
        {
            align: "center",
            title: String.manualFileName,
            dataIndex: "rentManualFileName",
            key: "rentManualFileName",
            render: (_, { rentManualId, rentManualFileName }) => {
                return (
                    <a
                        onClick={() => {
                            handleDownloadRentManualOk(rentManualId, rentManualFileName);
                        }}
                    >
                        <FilePdfOutlined /> {rentManualFileName}
                    </a>
                );
            },
        },
    ];

    const requestAxiosGetRentManuals = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_MANUAL_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetRentManuals,
        error: errorGetRentManuals,
        data: resultGetRentManuals,
        execute: requestGetRentManuals,
    } = useAsyncAxios(requestAxiosGetRentManuals);

    useEffect(() => {
        if (resultGetRentManuals === null) return;

        setRentManuals(resultGetRentManuals.rentManuals);
    }, [resultGetRentManuals]);

    useEffect(() => {
        if (errorGetRentManuals === null) return;

        alert.setAlert(errorGetRentManuals.response.data.code, "Error", errorGetRentManuals.response.data.message);
    }, [errorGetRentManuals]);

    const requestAxiosRemoveRentManual = async (data: Types.RentManualDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_MANUAL_URL + "/" + data.rentManualId);
        return response.data;
    };

    const {
        loading: loadingRemoveRentManual,
        error: errorRemoveRentManual,
        data: resultRemoveRentManual,
        execute: requestRemoveRentManuals,
    } = useAsyncAxios(requestAxiosRemoveRentManual);

    useEffect(() => {
        if (resultRemoveRentManual === null) return;

        alert.setAlert(AlertType.SUCCESS, "매뉴얼 삭제 성공", "매뉴얼 삭제를 완료하였습니다.");

        requestGetRentManuals();
    }, [resultRemoveRentManual]);

    useEffect(() => {
        if (errorRemoveRentManual === null) return;

        alert.setAlert(errorRemoveRentManual.response.data.code, "Error", errorRemoveRentManual.response.data.message);
    }, [errorGetRentManuals]);

    useEffect(() => {
        requestGetRentManuals();
    }, []);

    const onRow = (value: Types.RentManualDto) => {
        setEditMode("none");
        setcurrentRentManual(value);
    };

    const handleDeleteRentManual = () => {
        if (currentRentManual !== undefined) setOpenDeleteRentManual(true);
    };

    const handleDeleteRentManualOk = () => {
        setOpenDeleteRentManual(false);
        requestRemoveRentManuals(currentRentManual);
    };

    const handleDeleteRentManualCancel = () => {
        setOpenDeleteRentManual(false);
    };

    const handleDownloadRentManualOk = (rentManualId: number, rentManualFileName: string) => {
        console.log("id", rentManualId);

        const token = JSON.parse(Utils.getLocalStorage(Common.CONTEXT_AUTH) || "{}").token;

        fetch(Request.MANUAL_DOWNLOAD_URL + "/" + rentManualId, {
            headers: {
                "Content-Type": "application/octet-stream",
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            response.arrayBuffer().then((arrayBuffer) => {
                saveByteArray(rentManualFileName, new Uint8Array(arrayBuffer));
            });
        });
    };

    function saveByteArray(fileName: any, byte: any) {
        const blob = new Blob([byte], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    }

    const onRegisterRentManual = () => {
        requestGetRentManuals();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>매뉴얼</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteRentManual()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="rentManualId" dataSource={rentManuals} onRow={onRow} />
            {editMode === "add" && <RegisterRentManual editMode={editMode} onRegisterRentManual={onRegisterRentManual} />}
            {editMode !== "add" && currentRentManual !== undefined && (
                <RegisterRentManual editMode={editMode} rentManual={currentRentManual} onRegisterRentManual={onRegisterRentManual} />
            )}

            {openDeleteRentManual && (
                <Modal open={openDeleteRentManual} onOk={handleDeleteRentManualOk} onCancel={handleDeleteRentManualCancel}>
                    {currentRentManual?.rentManualName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default RentManualList;
